import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { queryStringToObject, validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import LoadingSpinner from '../../components/LoadingSpinner';
import LoginCreateModal from '../Login/LoginCreateModal';
import GlobalModal from '../../components/GlobalModal';

// ICONS
import Button from '../../components/Button';

// TYPES
import { IncidentArchiveLinkRequestRequestDetails, IncidentArchiveLinkIncidentCommand } from '../../api/models';

interface IncidentReportLinkingTypes {}

const IncidentReportLinking: React.FC<IncidentReportLinkingTypes> = ({}) => {
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const isLoggedIn = useAppSelector((state) => state.user.loggedIn);

	const dispatch = useAppDispatch();

	const history = useHistory();

	const [mounted, setMounted] = React.useState<boolean>(false);

	// State to control Modal
	const [modalIsOpen, setModalIsOpen] = React.useState(false);

	// State for Selected Plan Id
	const [selectedPlanId, setSelectedPlanId] = React.useState<string>();

	//Get incidentRecipientId from params
	const queryParams = queryStringToObject();

	const recipientId = queryParams.incidentRecipientId || '';

	const returnUrl = `/incident-invite?incidentRecipientId=${recipientId}`;

	const previousUrl = returnUrl;

	//Set IncidentLinkData
	const initialIncidentLinkData: IncidentArchiveLinkRequestRequestDetails = {
		organisationId: undefined,
		organisationIdentifier: undefined,
		organisation: undefined,
		organisationImage: undefined,
		isAuthenticated: undefined,
		isMemberOfOrganisation: undefined,
		switchProfileId: undefined,
		incidentId: undefined,
		isAssigned: undefined
	};
	const [incidentLinkingData, setIncidentLinkingData] =
		React.useState<IncidentArchiveLinkRequestRequestDetails>(initialIncidentLinkData);

	const getLinkRequestDetails = async () => {
		setIsLoading(true);

		const response = await apibridge.getIncidentLinkRequest(recipientId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setIncidentLinkingData(response.result);

			if (response.result.isAssigned === true && response.result.incidentId) {
				history.push(`/incident-reports/${response.result.incidentId}`);
			} else if (response.result.isAuthenticated && response.result.isMemberOfOrganisation) {
				doSwitchProfile(response.result.switchProfileId);
			}
		}
	};

	//Get current active profile
	const isActiveOrganisation = useAppSelector((state) => state.user.activeProfileId);

	const doSwitchProfile = async (switchProfileId: string) => {
		setIsLoading(true);

		if (isActiveOrganisation === switchProfileId) {
			setIsLoading(false);

			return;
		} else {
			const response = await apibridge.doSwitchOrganisation(switchProfileId);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				dispatch(UserSlice.actions.forceUpdate({}));
			}
		}
	};

	React.useEffect(() => {
		if (recipientId) {
			getLinkRequestDetails();
		}

		setMounted(true);

		setIsLoading(false);
	}, []);

	//Submit report

	const submitReport = async () => {
		setIsLoading(true);

		const data: IncidentArchiveLinkIncidentCommand = {
			recipientId: recipientId.toLowerCase()
		};

		const response = await apibridge.postIncidentLinkIncident(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/incident-reports');
		}
	};

	return (
		<div className="page-link-account">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="header row">
									<div className="section-header col-md-6 col-xl-4 col-12">
										<CircleImage extraClass="center-image" size={82} src={incidentLinkingData.organisationImage} />

										<h1>{incidentLinkingData.organisation} has shared an incident report with you</h1>

										{!incidentLinkingData.isAuthenticated && (
											<div className="button-container col-8">
												<Button
													onClick={() => {
														setModalIsOpen(!modalIsOpen);
													}}
													type="button"
													className="btn btn-primary full-width"
												>
													Log in or Sign up to view
												</Button>
											</div>
										)}

										{incidentLinkingData.isAuthenticated && incidentLinkingData.isMemberOfOrganisation && (
											<div className="button-container col-8">
												<Button
													onClick={() => {
														submitReport();
													}}
													type="button"
													className="btn btn-primary full-width"
												>
													Add report to my account and view
												</Button>
											</div>
										)}

										{incidentLinkingData.isAuthenticated && !incidentLinkingData.isMemberOfOrganisation && (
											<div className="button-container col-8">
												<Button
													onClick={() => {
														history.push(
															`/find-organisation?id=${incidentLinkingData.organisationIdentifier}&name=${incidentLinkingData.organisation}`
														);
													}}
													type="button"
													className="btn btn-primary full-width"
												>
													Join organisation
												</Button>
											</div>
										)}
									</div>
								</div>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal className="w-auto" useDefaultStructure={false} isOpen={modalIsOpen} setIsOpen={setModalIsOpen}>
				<LoginCreateModal
					previousUrl={previousUrl}
					returnUrl={returnUrl}
					paymentPlanId={selectedPlanId}
					organisationIdentifier={incidentLinkingData.organisationIdentifier}
				/>
			</GlobalModal>
		</div>
	);
};

export default IncidentReportLinking;
